import React, { useState, useEffect } from 'react';

export interface Gift {
  id: string;
  name: string;
  icon: string;
  price: number;
  animation: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary' | 'mythical';
  description: string;
}

interface SentGift {
  id: string;
  giftId: string;
  sender: string;
  receiver: string;
  timestamp: Date;
  message?: string;
}

interface StreamGiftsProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSendGift?: (gift: Gift) => void;
  balance?: number;
  recipientName?: string;
  onBuyCoins?: () => void;
}

const StreamGifts: React.FC<StreamGiftsProps> = ({ isOpen, onClose, onSendGift, balance, recipientName, onBuyCoins }) => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [giftMessage, setGiftMessage] = useState('');
  const [userCoins, setUserCoins] = useState(balance ?? 100);
  const [showAnimation, setShowAnimation] = useState<SentGift | null>(null);

  const giftsData: Gift[] = [
    { id: 'chocolate', name: 'Çikolata', icon: '🍫', price: 10, animation: 'bounce', rarity: 'common', description: 'Tatlı bir çikolata' },
    { id: 'coffee', name: 'Kahve', icon: '☕', price: 20, animation: 'steam', rarity: 'common', description: 'Sıcak bir kahve' },
    { id: 'applaud', name: 'Alkış', icon: '👏', price: 30, animation: 'pulse', rarity: 'common', description: 'Bravo!' },
    { id: 'rose', name: 'Gül', icon: '🌹', price: 50, animation: 'roseBloom', rarity: 'common', description: 'Güzel bir gül gönder' },
    { id: 'bluepot', name: 'Mavi Tik', icon: '☑️', price: 75, animation: 'checkAppear', rarity: 'rare', description: 'Doğrulanmış kullanıcı ol' },
    { id: 'diamond', name: 'Elmas', icon: '💎', price: 100, animation: 'diamondShine', rarity: 'rare', description: 'Parlak elmas gönder' },
    { id: 'moneygun', name: 'Para Tabancası', icon: '💸', price: 150, animation: 'moneyRain', rarity: 'rare', description: 'Paraları saç!' },
    { id: 'bear', name: 'Ayıcık', icon: '🐻', price: 1200, animation: 'bearHug', rarity: 'epic', description: 'Kocaman bir ayıcık' },
    { id: 'sportscar', name: 'Spor Araba', icon: '🏎️', price: 2000, animation: 'carDrive', rarity: 'legendary', description: 'Hızlı bir spor araba' },
    { id: 'lion', name: 'Aslan', icon: '🦁', price: 3500, animation: 'lionRoar', rarity: 'legendary', description: 'Kralın kükremesi' },
    { id: 'yacht', name: 'Yat', icon: '🛥️', price: 4000, animation: 'yachtFloat', rarity: 'legendary', description: 'Lüks yat gezisi' },
    { id: 'castle', name: 'Şato', icon: '🏰', price: 10000, animation: 'castleRise', rarity: 'mythical', description: 'Kendi krallığını kur' },
  ];

  useEffect(() => {
    setGifts(giftsData);
  }, []);

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#b0bec5';
      case 'rare': return '#4fc3f7';
      case 'epic': return '#ba68c8';
      case 'legendary': return '#ffb74d';
      case 'mythical': return '#e040fb';
      default: return '#b0bec5';
    }
  };

  const getRarityGlow = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'none';
      case 'rare': return '0 0 10px rgba(79, 195, 247, 0.3)';
      case 'epic': return '0 0 15px rgba(186, 104, 200, 0.4)';
      case 'legendary': return '0 0 20px rgba(255, 183, 77, 0.5)';
      case 'mythical': return '0 0 30px rgba(224, 64, 251, 0.6)';
      default: return 'none';
    }
  };

  const sendGift = () => {
    if (!selectedGift || userCoins < selectedGift.price) return;

    const newGift: SentGift = {
      id: Date.now().toString(),
      giftId: selectedGift.id,
      sender: 'Sen',
      receiver: recipientName || 'Yayıncı',
      timestamp: new Date(),
      message: giftMessage
    };

    setUserCoins(prev => prev - selectedGift.price);
    setShowAnimation(newGift);

    if (onSendGift) {
      onSendGift(selectedGift);
    }

    setGiftMessage('');
    if (onClose) {
      onClose();
    }

    setTimeout(() => setShowAnimation(null), 3000);
  };

  const buyCoins = (amount: number) => {
    // setUserCoins(prev => prev + amount); // Removed dummy add
    if (onBuyCoins) onBuyCoins();
  };

  return (
    <>
      {/* Gift Animation Overlay */}
      {showAnimation && (
        <div style={styles.animationOverlay}>
          <div style={styles.giftAnimation}>
            <span style={{
              ...styles.animationIcon,
              animation: `${gifts.find(g => g.id === showAnimation.giftId)?.animation || 'pulse'} 2.5s ease-in-out forwards`
            }}>
              {gifts.find(g => g.id === showAnimation.giftId)?.icon}
            </span>
            <div style={styles.animationText}>
              <div style={styles.animationSender}>{showAnimation.sender}</div>
              <div style={styles.animationGift}>
                {showAnimation.receiver}'a {gifts.find(g => g.id === showAnimation.giftId)?.name} gönderdi!
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Bottom Sheet */}
      {isOpen && (
        <div style={styles.overlay} onClick={onClose}>
          <div style={styles.bottomSheet} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                <h3 style={styles.title}>🎁 Hediyeler</h3>
                {recipientName && (
                  <span style={{ fontSize: '13px', color: '#ff007f', marginLeft: '10px', fontWeight: '600', background: 'rgba(233,30,99,0.1)', padding: '4px 8px', borderRadius: '8px' }}>
                    Alıcı: {recipientName}
                  </span>
                )}
              </div>
              <div style={styles.headerRight}>
                <div style={styles.coinBadge}>
                  <span style={styles.coinIcon}>🪙</span>
                  <span style={styles.coinAmount}>{userCoins}</span>
                  <button onClick={() => buyCoins(100)} style={styles.addButton}>+</button>
                </div>
                <button onClick={onClose} style={styles.closeButton}>✕</button>
              </div>
            </div>

            {/* Gifts Grid */}
            <div style={styles.gridContainer} className="hide-scrollbar">
              <div style={styles.grid}>
                {gifts.map(gift => {
                  const isSelected = selectedGift?.id === gift.id;
                  const canAfford = userCoins >= gift.price;
                  return (
                    <button
                      key={gift.id}
                      onClick={() => setSelectedGift(gift)}
                      style={{
                        ...styles.giftCard,
                        borderColor: isSelected ? getRarityColor(gift.rarity) : 'transparent',
                        background: isSelected ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
                        boxShadow: isSelected ? getRarityGlow(gift.rarity) : 'none',
                        opacity: canAfford ? 1 : 0.5
                      }}
                    >
                      <div style={styles.giftIcon}>{gift.icon}</div>
                      <div style={styles.giftName}>{gift.name}</div>
                      <div style={{ ...styles.giftPrice, color: isSelected ? '#fff' : '#aaa' }}>
                        🪙 {gift.price}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer / Send Area */}
            {selectedGift && (
              <div style={styles.footer}>
                <div style={styles.selectedInfo}>
                  <div style={styles.selectedDetails}>
                    <span style={styles.footerGiftName}>{selectedGift.icon} {selectedGift.name}</span>
                    <span style={styles.footerPrice}>Toplam: 🪙 {selectedGift.price}</span>
                  </div>
                </div>
                <button
                  onClick={sendGift}
                  disabled={userCoins < selectedGift.price}
                  style={{
                    ...styles.sendButton,
                    background: userCoins >= selectedGift.price
                      ? 'linear-gradient(135deg, #ff007f 0%, #ff4081 100%)'
                      : '#555',
                    cursor: userCoins >= selectedGift.price ? 'pointer' : 'not-allowed'
                  }}
                >
                  GÖNDER
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 10010, // Higher than VoiceChat base
    display: 'flex',
    alignItems: 'flex-end',
    backdropFilter: 'blur(3px)',
  },
  bottomSheet: {
    width: '100%',
    backgroundColor: 'rgba(20, 20, 30, 0.95)',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    padding: '20px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
    margin: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  coinBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(255,193,7,0.15)',
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid rgba(255,193,7,0.3)',
  },
  coinIcon: {
    fontSize: '14px',
  },
  coinAmount: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#ffc107',
  },
  addButton: {
    backgroundColor: '#ffc107',
    color: '#000',
    border: 'none',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '4px',
  },
  closeButton: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridContainer: {
    overflowY: 'auto',
    maxHeight: '400px',
    padding: '4px', // Space for shadows
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    paddingBottom: '20px',
  },
  giftCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 4px',
    borderRadius: '16px',
    border: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  giftIcon: {
    fontSize: '32px',
    marginBottom: '8px',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  },
  giftName: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#ddd',
    marginBottom: '4px',
    whiteSpace: 'nowrap',
  },
  giftPrice: {
    fontSize: '10px',
    fontWeight: 'bold',
  },
  footer: {
    marginTop: '10px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selectedInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  selectedDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  footerGiftName: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  footerPrice: {
    color: '#ff007f',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  sendButton: {
    padding: '12px 32px',
    borderRadius: '24px',
    border: 'none',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    boxShadow: '0 4px 15px rgba(255, 0, 127, 0.4)',
  },
  animationOverlay: {
    position: 'absolute', // Changed from fixed
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 11000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  giftAnimation: {
    textAlign: 'center',
    animation: 'giftBurst 1s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
  animationIcon: {
    fontSize: '100px',
    display: 'block',
    marginBottom: '16px',
    filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.8))',
    willChange: 'transform, opacity',
  },
  animationText: {
    background: 'rgba(0,0,0,0.6)',
    padding: '12px 24px',
    borderRadius: '20px',
    backdropFilter: 'blur(5px)',
  },
  animationSender: {
    color: '#ffc107',
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  animationGift: {
    color: '#fff',
    fontSize: '16px',
  },
};

// Add global styles for animations if not exists
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes giftBurst {
    0% { transform: scale(0) translateY(50px); opacity: 0; filter: blur(10px); }
    40% { transform: scale(1.2) translateY(-20px); opacity: 1; filter: blur(0px); }
    70% { transform: scale(0.95) translateY(10px); }
    100% { transform: scale(1) translateY(0); opacity: 1; filter: drop-shadow(0 0 20px rgba(255,255,255,0.4)); }
  }
  @keyframes steam {
    0% { transform: translateY(0) scale(0.8); opacity: 0; }
    20% { opacity: 1; transform: translateY(-10px) scale(1); }
    80% { opacity: 1; transform: translateY(-40px) scale(1.5); filter: blur(2px); }
    100% { transform: translateY(-60px) scale(2); opacity: 0; filter: blur(5px); }
  }
  @keyframes pulse {
    0% { transform: scale(1); filter: drop-shadow(0 0 0px white); }
    50% { transform: scale(1.6); filter: drop-shadow(0 0 30px #ff007f); }
    100% { transform: scale(1); filter: drop-shadow(0 0 0px white); }
  }
  @keyframes checkAppear {
    0% { transform: scale(0) rotate(-180deg); opacity: 0; }
    50% { transform: scale(1.8) rotate(15deg); opacity: 1; filter: drop-shadow(0 0 40px cyan); }
    70% { transform: scale(1.4) rotate(-5deg); }
    100% { transform: scale(1.5) rotate(0deg); opacity: 1; filter: drop-shadow(0 0 20px blue); }
  }
  @keyframes moneyRain {
    0% { transform: translateY(-100px) scale(0.5); opacity: 0; }
    20% { transform: translateY(-20px) scale(1.5); opacity: 1; filter: drop-shadow(0 0 30px #00ff00); }
    80% { transform: translateY(100px) scale(1.5) rotate(360deg); opacity: 1; }
    100% { transform: translateY(200px) scale(1) rotate(720deg); opacity: 0; }
  }
  @keyframes carDrive {
    0% { transform: translateX(-150%) scale(0.8) skewX(20deg); opacity: 0; }
    20% { transform: translateX(-50%) scale(1) skewX(10deg); opacity: 1; }
    50% { transform: translateX(0) scale(1.8) skewX(0deg); opacity: 1; filter: drop-shadow(0 15px 25px rgba(255, 0, 0, 0.6)); }
    80% { transform: translateX(50%) scale(1.2) skewX(-10deg); opacity: 1; }
    100% { transform: translateX(150%) scale(0.8) skewX(-20deg); opacity: 0; }
  }
  @keyframes lionRoar {
    0% { transform: scale(0.1); opacity: 0; }
    20% { transform: scale(1.6); opacity: 1; filter: drop-shadow(0 0 30px gold); }
    30% { transform: scale(2) rotate(-15deg); }
    40% { transform: scale(2) rotate(15deg); filter: drop-shadow(0 0 60px orange); }
    50% { transform: scale(2) rotate(-15deg); }
    60% { transform: scale(2) rotate(15deg); filter: drop-shadow(0 0 60px orange); }
    70% { transform: scale(1.8) rotate(0deg); filter: drop-shadow(0 0 30px gold); }
    100% { transform: scale(3); opacity: 0; filter: blur(15px); }
  }
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0) scale(1); }
    40% { transform: translateY(-40px) scale(1.2); filter: drop-shadow(0 15px 15px rgba(0,0,0,0.2)); }
    60% { transform: translateY(-20px) scale(1.1); }
  }
  @keyframes roseBloom {
    0% { transform: scale(0.2) rotate(-45deg); opacity: 0; filter: drop-shadow(0 0 0px red); }
    50% { transform: scale(2) rotate(15deg); opacity: 1; filter: drop-shadow(0 0 40px red); }
    100% { transform: scale(1.5) rotate(0deg); opacity: 0; filter: drop-shadow(0 0 20px pink); }
  }
  @keyframes bearHug {
    0% { transform: scale(0.2); opacity: 0; }
    30% { transform: scale(1.5) rotate(-15deg); opacity: 1; }
    50% { transform: scale(1.3) rotate(15deg); }
    70% { transform: scale(1.8) rotate(-10deg); filter: drop-shadow(0 0 50px #FF9800); }
    100% { transform: scale(2.2); opacity: 0; filter: blur(10px); }
  }
  @keyframes castleRise {
    0% { transform: translateY(100px) scale(0.3); opacity: 0; filter: brightness(0.5); }
    40% { transform: translateY(0) scale(1.8); opacity: 1; filter: brightness(1) drop-shadow(0 20px 40px rgba(0,0,0,0.6)); }
    70% { transform: translateY(-10px) scale(2); opacity: 1; filter: brightness(1.2) drop-shadow(0 0 80px gold); }
    100% { transform: translateY(-50px) scale(2.5); opacity: 0; filter: blur(10px); }
  }
  @keyframes diamondShine {
    0% { transform: scale(0) rotate(0deg); opacity: 0; }
    50% { transform: scale(2.5) rotate(180deg); opacity: 1; filter: drop-shadow(0 0 50px cyan); }
    100% { transform: scale(1.5) rotate(360deg); opacity: 0; filter: drop-shadow(0 0 20px blue); }
  }
  @keyframes yachtFloat {
    0% { transform: translateX(-150%) translateY(30px) scale(0.8); opacity: 0; }
    30% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1.2); }
    50% { transform: translateX(0) translateY(-20px) scale(1.5); filter: drop-shadow(0 20px 30px rgba(0,255,255,0.4)); }
    70% { opacity: 1; transform: translateX(50%) translateY(0) scale(1.2); }
    100% { transform: translateX(150%) translateY(30px) scale(0.8); opacity: 0; }
  }
`;
if (!document.getElementById('gift-animations')) {
  styleSheet.id = 'gift-animations';
  document.head.appendChild(styleSheet);
}

export default StreamGifts;

